import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { medicationService, CreatePatientMedicationInput } from '../services/medicationService'
import { MedicationReference } from '../types/medication'
import './MedicationPrescriber.css'

interface MedicationPrescriberProps {
  patientId: string
}

export function MedicationPrescriber({ patientId }: MedicationPrescriberProps) {
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [results, setResults] = useState<MedicationReference[]>([])
  const [activeIndex, setActiveIndex] = useState<number>(-1)
  const [selectedQuantities, setSelectedQuantities] = useState<Record<string, number>>({}) // drugIdPK -> multiplier
  const [, setSelected] = useState<MedicationReference | null>(null) // Used internally for form state management
  const searchTimeoutRef = useRef<number | undefined>(undefined)
  const [form, setForm] = useState<CreatePatientMedicationInput>({
    name: '',
    referenceDrugId: undefined,
    doseText: '',
    route: '',
    frequency: { text: '' },
    quantity: { value: undefined, unit: '' },
    indication: '',
  })

  const prescribeMutation = useMutation({
    mutationFn: (payload: CreatePatientMedicationInput) =>
      medicationService.createPatientMedication(patientId, payload),
    onSuccess: async () => {
      // Invalidate and refetch all patient-medications queries for this patient
      await queryClient.invalidateQueries({ 
        queryKey: ['patient-medications', patientId],
        exact: false 
      })
      // Also explicitly refetch to ensure immediate update
      await queryClient.refetchQueries({ 
        queryKey: ['patient-medications', patientId],
        exact: false 
      })
      setSelected(null)
      setForm({
        name: '',
        referenceDrugId: undefined,
        doseText: '',
        route: '',
        frequency: { text: '' },
        quantity: { value: undefined, unit: '' },
        indication: '',
      })
    },
  })

  const performSearch = async (term: string) => {
    const trimmedTerm = term.trim()
    if (!trimmedTerm) {
      setResults([])
      setActiveIndex(-1)
      setSelectedQuantities({})
      return
    }
    
    // Require minimum 3 characters to prevent huge result sets
    if (trimmedTerm.length < 3) {
      setResults([])
      setActiveIndex(-1)
      setSelectedQuantities({})
      return
    }
    
    try {
      const res = await medicationService.searchReference(trimmedTerm)
      setResults(res.items)
      if (res.items.length > 0) {
        setActiveIndex(0)
        // Set default quantity (1) for all medications that have packSize
        const defaultQuantities: Record<string, number> = {}
        res.items.forEach((med) => {
          if (med.packSize) {
            defaultQuantities[med.drugIdPK] = 1
          }
        })
        setSelectedQuantities((prev) => ({ ...prev, ...defaultQuantities }))
      } else {
        setActiveIndex(-1)
      }
    } catch (err) {
      console.error('Failed to search medications', err)
      alert('Failed to search medications.')
    }
  }

  useEffect(() => {
    if (searchTimeoutRef.current !== undefined) {
      window.clearTimeout(searchTimeoutRef.current)
    }

    const term = searchTerm.trim()
    if (!term || term.length < 3) {
      setResults([])
      setActiveIndex(-1)
      return
    }

    const timeoutId = window.setTimeout(() => {
      void performSearch(term)
    }, 300)
    searchTimeoutRef.current = timeoutId

    return () => {
      if (searchTimeoutRef.current !== undefined) {
        window.clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchTerm])

  const getQuantityUnit = (med: MedicationReference): string => {
    const form = (med.dosageForm || '').toLowerCase()
    if (form.includes('tablet')) return 'tablets'
    if (form.includes('capsule')) return 'capsules'
    if (form.includes('ml')) return 'ml'
    if (form.includes('sachet')) return 'sachets'
    if (form.includes('inhaler')) return 'inhalers'
    return 'units'
  }

  const handleSelect = (med: MedicationReference) => {
    setSelected(med)
    // Use selected quantity or default to 1
    const multiplier = selectedQuantities[med.drugIdPK] ?? 1
    setForm((prev) => ({
      ...prev,
      referenceDrugId: med.drugIdPK,
      name: med.productName,
      route: (med.routesOfAdministration && med.routesOfAdministration[0]) || '',
      quantity:
        med.packSize != null
          ? {
              value: med.packSize * multiplier,
              unit: getQuantityUnit(med),
            }
          : prev.quantity,
    }))
  }

  const handleSelectWithQuantity = (med: MedicationReference, multiplier: number) => {
    setSelected(med)
    setSelectedQuantities((prev) => ({ ...prev, [med.drugIdPK]: multiplier }))
    setForm((prev) => ({
      ...prev,
      referenceDrugId: med.drugIdPK,
      name: med.productName,
      route: (med.routesOfAdministration && med.routesOfAdministration[0]) || '',
      quantity:
        med.packSize != null
          ? {
              value: med.packSize * multiplier,
              unit: getQuantityUnit(med),
            }
          : prev.quantity,
    }))
  }

  // Group results by interchangeableListCode
  const groupedResults = (() => {
    const groups: Map<string | null, MedicationReference[]> = new Map()
    const ungrouped: MedicationReference[] = []

    results.forEach((med) => {
      const iccode = med.interchangeableListCode || null
      if (iccode) {
        if (!groups.has(iccode)) {
          groups.set(iccode, [])
        }
        groups.get(iccode)!.push(med)
      } else {
        ungrouped.push(med)
      }
    })

    // Convert to array format: [groupKey, medications[]]
    const groupArray: Array<{ key: string | null; medications: MedicationReference[]; isInterchangeable: boolean }> = []
    
    // Add grouped medications first
    groups.forEach((medications, key) => {
      groupArray.push({ key, medications, isInterchangeable: true })
    })
    
    // Add ungrouped medications
    ungrouped.forEach((med) => {
      groupArray.push({ key: null, medications: [med], isInterchangeable: false })
    })

    return groupArray.slice(0, 10) // Limit to 10 groups
  })()

  // Flatten for keyboard navigation
  const flatResults: MedicationReference[] = []
  groupedResults.forEach((group) => {
    flatResults.push(...group.medications)
  })
  const visibleResults = flatResults.slice(0, 30) // Show more items since we're grouping

  const handleQuickAdd = async (med: MedicationReference, multiplier: number = 1) => {
    const payload: CreatePatientMedicationInput = {
      referenceDrugId: med.drugIdPK,
      name: med.productName,
      route: (med.routesOfAdministration && med.routesOfAdministration[0]) || '',
      quantity:
        med.packSize != null
          ? {
              value: med.packSize * multiplier,
              unit: getQuantityUnit(med),
            }
          : undefined,
      startDate: new Date().toISOString().split('T')[0],
    }
    
    try {
      await prescribeMutation.mutateAsync(payload)
      // Clear search and reset
      setSearchTerm('')
      setResults([])
      setActiveIndex(-1)
      setSelectedQuantities({})
    } catch {
      // Error already surfaced
    }
  }

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!visibleResults.length) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((prev) => {
        const newIndex = prev < 0 ? 0 : (prev + 1) % visibleResults.length
        return newIndex
      })
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((prev) => {
        const newIndex = prev <= 0 ? visibleResults.length - 1 : prev - 1
        return newIndex
      })
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < visibleResults.length) {
        e.preventDefault()
        const med = visibleResults[activeIndex]
        // Use selected quantity or default to 1
        const multiplier = selectedQuantities[med.drugIdPK] ?? 1
        // Directly add the medication to the list
        handleQuickAdd(med, multiplier)
      }
    } else if (e.key === 'Escape') {
      setResults([])
      setActiveIndex(-1)
      setSelectedQuantities({})
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      alert('Medication name is required.')
      return
    }
    try {
      // Set startDate to today if not provided
      const payload = {
        ...form,
        startDate: form.startDate || new Date().toISOString().split('T')[0],
      }
      await prescribeMutation.mutateAsync(payload)
    } catch {
      // Error already surfaced
    }
  }

  const handleFreeText = () => {
    setSelected(null)
    setForm({
      name: '',
      referenceDrugId: undefined,
      doseText: '',
      route: '',
      frequency: { text: '' },
      quantity: { value: undefined, unit: '' },
      indication: '',
    })
  }

  return (
    <div className="medication-prescriber">
      <div className="prescriber-header">
        <h3>Prescribe medication</h3>
        <p className="prescriber-subtitle">
          Search the medication terminology or add a free-text medication.
        </p>
      </div>

      <div className="prescriber-search-row">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          placeholder="Search by name, ATC, or code (min 3 characters, use ↑↓ to navigate, Enter to add)"
          aria-label="Search medications"
        />
        <button type="button" className="btn-link" onClick={handleFreeText}>
          Free-text medication
        </button>
      </div>

      {groupedResults.length > 0 && (
        <div className="prescriber-results">
          {groupedResults.map((group) => {
            const isInterchangeable = group.isInterchangeable && group.medications.length > 1
            const maxVisibleRows = 3
            const hasMoreItems = group.medications.length > maxVisibleRows
            return (
              <div key={group.key || 'ungrouped'} className="medication-group">
                {isInterchangeable && (
                  <div className="interchangeable-header">
                    <span className="interchangeable-badge">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 1L9.09 4.26L13 5.27L10 8.14L10.18 12.02L7 10.77L3.82 12.02L4 8.14L1 5.27L4.91 4.26L7 1Z" fill="currentColor"/>
                      </svg>
                      Interchangeable Group ({group.medications.length} alternatives)
                    </span>
                  </div>
                )}
                <div className={`medication-group-table-wrapper ${hasMoreItems ? 'has-scroll' : ''}`}>
                  <table>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Form</th>
                        <th>Route</th>
                        <th>ATC</th>
                        <th>Quantity options</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {group.medications.map((r) => {
                        const flatIndex = flatResults.findIndex((med) => med.drugIdPK === r.drugIdPK)
                        const isActive = flatIndex === activeIndex
                        return (
                        <tr
                          key={r.drugIdPK}
                          className={`${isActive ? 'active' : ''} ${isInterchangeable ? 'interchangeable-row' : ''}`}
                          onMouseEnter={() => setActiveIndex(flatIndex)}
                        >
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <strong>{r.productName}</strong>
                                {isInterchangeable && (
                                  <span className="interchangeable-indicator" title="Interchangeable medication">
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M6 0.5L7.36 3.22L10.5 3.66L8.25 5.78L8.73 8.91L6 7.75L3.27 8.91L3.75 5.78L1.5 3.66L4.64 3.22L6 0.5Z" fill="currentColor"/>
                                    </svg>
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>{r.dosageForm || '-'}</td>
                            <td>{(r.routesOfAdministration && r.routesOfAdministration.join(', ')) || '-'}</td>
                            <td>{(r.atcCodes && r.atcCodes.join(', ')) || '-'}</td>
                          <td>
                            {r.packSize ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center' }}>
                                {[1, 2, 3].map((mult) => {
                                  // Selected if explicitly set, or if it's the first option (mult=1) by default
                                  const currentSelection = selectedQuantities[r.drugIdPK]
                                  const isSelected = currentSelection === mult || 
                                                    (currentSelection === undefined && mult === 1)
                                  return (
                                    <button
                                      key={mult}
                                      type="button"
                                      className={`btn-pill-option ${isSelected ? 'selected' : ''}`}
                                      onClick={() => handleSelectWithQuantity(r, mult)}
                                      title={`Select ${mult} × ${r.packSize} ${getQuantityUnit(r)}`}
                                    >
                                      {mult}×{r.packSize}
                                    </button>
                                  )
                                })}
                              </div>
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                            <td>
                              <button
                                type="button"
                                className="btn-link small"
                                onClick={() => handleSelect(r)}
                              >
                                Select
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                  {hasMoreItems && (
                    <div className="scroll-indicator">
                      <span className="scroll-indicator-text">
                        Scroll for more ({group.medications.length - maxVisibleRows} more)
                      </span>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <form className="prescriber-form" onSubmit={handleSubmit}>
        <div className="prescriber-form-grid">
          <div className="form-group full-width">
            <label htmlFor="med-name">Medication name</label>
            <input
              id="med-name"
              type="text"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              placeholder="e.g. Paracetamol 500mg Tablets"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="med-dose">Dose</label>
            <input
              id="med-dose"
              type="text"
              value={form.doseText || ''}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  doseText: e.target.value,
                }))
              }
              placeholder="e.g. 1 tablet"
            />
          </div>

          <div className="form-group">
            <label htmlFor="med-route">Route</label>
            <input
              id="med-route"
              type="text"
              value={form.route || ''}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  route: e.target.value,
                }))
              }
              placeholder="e.g. Oral"
            />
          </div>

          <div className="form-group">
            <label htmlFor="med-frequency">Frequency</label>
            <input
              id="med-frequency"
              type="text"
              value={form.frequency?.text || ''}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  frequency: { ...(prev.frequency || {}), text: e.target.value },
                }))
              }
              placeholder="e.g. Every 6 hours as needed"
            />
          </div>

          <div className="form-group">
            <label htmlFor="med-quantity">Quantity</label>
            <input
              id="med-quantity"
              type="text"
              value={
                form.quantity?.value !== undefined && form.quantity?.unit
                  ? `${form.quantity.value} ${form.quantity.unit}`
                  : ''
              }
              onChange={(e) => {
                const [valueStr, ...unitParts] = e.target.value.split(' ')
                const value = parseFloat(valueStr)
                const unit = unitParts.join(' ')
                setForm((prev) => ({
                  ...prev,
                  quantity: {
                    value: isNaN(value) ? undefined : value,
                    unit: unit || prev.quantity?.unit,
                  },
                }))
              }}
              placeholder="e.g. 30 tablets"
            />
          </div>

          <div className="form-group">
            <label htmlFor="med-indication">Indication</label>
            <input
              id="med-indication"
              type="text"
              value={form.indication || ''}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  indication: e.target.value,
                }))
              }
              placeholder="Reason for prescribing (optional)"
            />
          </div>
        </div>

        <div className="prescriber-actions">
          <button
            type="submit"
            className="btn-primary"
            disabled={prescribeMutation.isPending}
          >
            {prescribeMutation.isPending ? 'Saving…' : 'Add to medication list'}
          </button>
        </div>
      </form>
    </div>
  )
}

